import { useEffect, useRef, useState } from 'react'
import {
  Sparkles, Loader2, AlertCircle, Play, History, Check, ChevronDown,
  FileText, Download, Trash2, ArrowLeft, RefreshCw, Info,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts'
import { api } from '../../utils/api'

// ── Types ────────────────────────────────────────────────────────────────────

type Period = 'daily' | 'monthly' | 'yearly' | 'custom'
type AiProvider = 'gemini' | 'claude' | 'openai' | 'groq'

interface Summary {
  period: Period; periodLabel: string; from: string; till: string
  totals: { created: number; resolved: number; open: number; carriedOver: number; overdueOpen: number }
  byStatus: { key: string; label: string; count: number }[]
  byPriority: { key: string; label: string; count: number }[]
  byType: { key: string; label: string; count: number }[]
  sla: {
    withSla: number; met: number; breached: number; compliancePercent: number | null
    byPriority: { priority: string; label: string; total: number; met: number; breached: number; pct: number | null }[]
  }
  resolution: { countResolved: number; avgHours: number | null; medianHours: number | null }
  categories: { name: string; count: number; breached: number; avgResolutionHours: number | null }[]
  trend: { bucket: string; created: number; resolved: number }[]
  topOverdue: {
    ticketNumber: number; title: string; priority: string; priorityLabel: string
    status: string; statusLabel: string; dueDate: string; assigneeName: string | null
  }[]
}
interface ReportListItem {
  id: string; period: Period; trigger: string; title: string
  provider?: string | null; status: string; error?: string | null
  createdAt: string; createdByName?: string | null
}
interface ReportDetail extends ReportListItem { summary: Summary | null; narrative: string | null }

const PERIODS: { key: Period; label: string; hint: string }[] = [
  { key: 'daily',   label: 'รายวัน',  hint: '24 ชม.ล่าสุด' },
  { key: 'monthly', label: 'รายเดือน', hint: '30 วันล่าสุด' },
  { key: 'yearly',  label: 'รายปี',   hint: '365 วันล่าสุด' },
  { key: 'custom',  label: 'กำหนดเอง', hint: 'เลือกช่วงวันที่' },
]
const PERIOD_LABEL: Record<Period, string> = { daily: 'รายวัน', monthly: 'รายเดือน', yearly: 'รายปี', custom: 'กำหนดเอง' }
const PROVIDER_LABEL: Record<AiProvider, string> = { gemini: 'Gemini', claude: 'Claude', openai: 'ChatGPT', groq: 'Groq' }

const STATUS_COLORS: Record<string, string> = { OPEN: '#3b82f6', ASSIGNED: '#6366f1', IN_PROGRESS: '#8b5cf6', PENDING: '#f59e0b', RESOLVED: '#10b981', CLOSED: '#6b7280', CANCELLED: '#f87171' }
const PRIORITY_COLORS: Record<string, string> = { CRITICAL: '#ef4444', HIGH: '#f97316', MEDIUM: '#eab308', LOW: '#6b7280' }

const fmtDateTime = (s?: string | null) =>
  s ? new Date(s).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '—'
const hrs = (n: number | null | undefined) => (n == null ? '—' : n >= 24 ? `${(n / 24).toFixed(1)} วัน` : `${n} ชม.`)

// ── Tiny markdown renderer (from ZabbixAnalysisPage) ─────────────────────────

function Markdown({ text }: { text: string }) {
  const lines = text.split('\n')
  const out: JSX.Element[] = []
  let list: string[] = []
  const inline = (s: string) =>
    s.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>
      if (p.startsWith('`') && p.endsWith('`')) return <code key={i} className="bg-gray-100 rounded px-1 font-mono text-[0.9em]">{p.slice(1, -1)}</code>
      return <span key={i}>{p}</span>
    })
  const flush = () => {
    if (!list.length) return
    out.push(<ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2">{list.map((li, i) => <li key={i}>{inline(li)}</li>)}</ul>)
    list = []
  }
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, '')
    if (/^#{1,6}\s/.test(line)) {
      flush()
      const level = line.match(/^#+/)![0].length
      const content = line.replace(/^#+\s/, '')
      out.push(level <= 2
        ? <h3 key={idx} className="text-base font-bold text-gray-900 mt-4 mb-1.5">{inline(content)}</h3>
        : <h4 key={idx} className="text-sm font-semibold text-gray-800 mt-3 mb-1">{inline(content)}</h4>)
    } else if (/^\s*[-*]\s+/.test(line)) list.push(line.replace(/^\s*[-*]\s+/, ''))
    else if (/^>\s?/.test(line)) {
      flush()
      out.push(<blockquote key={idx} className="border-l-4 border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 my-2 rounded-r-lg text-sm">{inline(line.replace(/^>\s?/, ''))}</blockquote>)
    } else if (line.trim() === '') flush()
    else { flush(); out.push(<p key={idx} className="my-1.5 leading-relaxed">{inline(line)}</p>) }
  })
  flush()
  return <div className="text-sm text-gray-700">{out}</div>
}

function Stat({ label, value, sub, danger }: { label: string; value: string; sub?: string; danger?: boolean }) {
  return (
    <div className={`rounded-xl border px-3.5 py-2.5 ${danger ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
      <div className="text-[11px] text-gray-400">{label}</div>
      <div className={`text-xl font-bold ${danger ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  )
}

// ── Download helpers ─────────────────────────────────────────────────────────

async function downloadWord(id: string) {
  const res = await api.get(`/servicedesk/reports/${id}/export.docx`, { responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `servicedesk-report-${id}.docx`
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}
function openPdf(id: string) {
  window.open(`/servicedesk/reports/${id}/print`, '_blank', 'noopener')
}

function DownloadButtons({ id }: { id: string }) {
  const [busy, setBusy] = useState(false)
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={async () => { setBusy(true); try { await downloadWord(id) } catch { alert('ดาวน์โหลดไม่สำเร็จ') } finally { setBusy(false) } }}
        disabled={busy}
        className="flex items-center gap-1.5 text-xs border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Word
      </button>
      <button onClick={() => openPdf(id)}
        className="flex items-center gap-1.5 text-xs border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg">
        <FileText size={13} /> PDF
      </button>
    </div>
  )
}

// ── Summary view ─────────────────────────────────────────────────────────────

function SummaryView({ summary, narrative, provider, streaming }: {
  summary: Summary | null; narrative: string; provider?: string | null; streaming?: boolean
}) {
  return (
    <div className="space-y-5">
      {summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Stat label="สร้างใหม่" value={String(summary.totals.created)} sub="ในช่วงนี้" />
            <Stat label="แก้ไขได้" value={String(summary.totals.resolved)} />
            <Stat label="ค้างอยู่" value={String(summary.totals.open)} sub={`ยกมา ${summary.totals.carriedOver}`} />
            <Stat label="เกิน SLA (ค้าง)" value={String(summary.totals.overdueOpen)} danger={summary.totals.overdueOpen > 0} />
            <Stat label="SLA Compliance" value={summary.sla.compliancePercent != null ? `${summary.sla.compliancePercent}%` : '—'}
              sub={`${summary.sla.met}/${summary.sla.withSla} ผ่าน`}
              danger={summary.sla.compliancePercent != null && summary.sla.compliancePercent < 70} />
          </div>

          {summary.trend.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">สร้าง vs แก้ไขได้</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={summary.trend} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="created" name="สร้าง" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="resolved" name="แก้ไขได้" fill="#10b981" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {summary.byStatus.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">ตามสถานะ</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={summary.byStatus} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} name="จำนวน">
                      {summary.byStatus.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.key] || '#6b7280'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {summary.byPriority.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">ตาม Priority</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={summary.byPriority} cx="50%" cy="50%" outerRadius={70} dataKey="count" nameKey="label"
                      label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                      {summary.byPriority.map((pp, i) => <Cell key={i} fill={PRIORITY_COLORS[pp.key] || '#6b7280'} />)}
                    </Pie>
                    <Tooltip /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {summary.sla.byPriority.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-800">SLA ตาม Priority</div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="text-left font-medium px-4 py-2">Priority</th>
                  <th className="text-right font-medium px-3 py-2">ทั้งหมด</th>
                  <th className="text-right font-medium px-3 py-2">ผ่าน</th>
                  <th className="text-right font-medium px-3 py-2">เกิน</th>
                  <th className="text-right font-medium px-4 py-2">Compliance</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {summary.sla.byPriority.map(r => (
                    <tr key={r.priority}>
                      <td className="px-4 py-2">
                        <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: (PRIORITY_COLORS[r.priority] || '#6b7280') + '22', color: PRIORITY_COLORS[r.priority] || '#6b7280' }}>{r.label}</span>
                      </td>
                      <td className="text-right px-3 py-2 tabular-nums font-semibold text-gray-900">{r.total}</td>
                      <td className="text-right px-3 py-2 tabular-nums text-green-600">{r.met}</td>
                      <td className="text-right px-3 py-2 tabular-nums text-red-600">{r.breached}</td>
                      <td className="text-right px-4 py-2 tabular-nums">
                        {r.pct != null && <span className={`font-bold ${r.pct >= 90 ? 'text-green-600' : r.pct >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{r.pct}%</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Stat label="เวลาแก้ไขเฉลี่ย" value={hrs(summary.resolution.avgHours)} sub={`${summary.resolution.countResolved} ticket`} />
            <Stat label="เวลาแก้ไข (มัธยฐาน)" value={hrs(summary.resolution.medianHours)} />
            <Stat label="Ticket มี SLA" value={String(summary.sla.withSla)} sub={`เกิน ${summary.sla.breached}`} />
          </div>

          {summary.categories.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-800">หมวดหมู่ปัญหายอดนิยม</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-gray-400 border-b border-gray-100">
                    <th className="text-left font-medium px-4 py-2">หมวดหมู่</th>
                    <th className="text-right font-medium px-3 py-2">จำนวน</th>
                    <th className="text-right font-medium px-3 py-2">เกิน SLA</th>
                    <th className="text-right font-medium px-4 py-2">เวลาแก้ไขเฉลี่ย</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {summary.categories.map((c, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-gray-800">{c.name}</td>
                        <td className="text-right px-3 py-2 tabular-nums font-semibold text-gray-900">{c.count}</td>
                        <td className="text-right px-3 py-2 tabular-nums text-red-600">{c.breached || '—'}</td>
                        <td className="text-right px-4 py-2 tabular-nums text-gray-500">{hrs(c.avgResolutionHours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {summary.topOverdue.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-800 flex items-center gap-2">
                <AlertCircle size={14} className="text-red-500" /> Ticket ค้างเกิน SLA ({summary.topOverdue.length})
              </div>
              <div className="overflow-x-auto max-h-72">
                <table className="w-full text-sm">
                  <thead className="bg-white sticky top-0"><tr className="text-xs text-gray-400 border-b border-gray-100">
                    <th className="text-left font-medium px-4 py-2">#</th>
                    <th className="text-left font-medium px-3 py-2">หัวข้อ</th>
                    <th className="text-left font-medium px-3 py-2">Priority</th>
                    <th className="text-left font-medium px-3 py-2">สถานะ</th>
                    <th className="text-left font-medium px-3 py-2">Due Date</th>
                    <th className="text-left font-medium px-4 py-2">ผู้รับผิดชอบ</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {summary.topOverdue.map(o => (
                      <tr key={o.ticketNumber} className="hover:bg-red-50/30">
                        <td className="px-4 py-2 font-mono text-xs text-gray-500">#{String(o.ticketNumber).padStart(4, '0')}</td>
                        <td className="px-3 py-2 max-w-xs"><p className="truncate font-medium text-gray-900">{o.title}</p></td>
                        <td className="px-3 py-2">
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: (PRIORITY_COLORS[o.priority] || '#6b7280') + '22', color: PRIORITY_COLORS[o.priority] || '#6b7280' }}>{o.priorityLabel}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{o.statusLabel}</td>
                        <td className="px-3 py-2 text-xs text-red-600 font-medium">{fmtDateTime(o.dueDate)}</td>
                        <td className="px-4 py-2 text-xs text-gray-600">{o.assigneeName || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {(narrative || streaming) && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center">
              <Sparkles size={13} className="text-white" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">บทวิเคราะห์จาก AI</h2>
            {provider && <span className="text-[10px] text-gray-400">· {provider}</span>}
          </div>
          {narrative
            ? <Markdown text={narrative} />
            : <div className="flex items-center gap-2 text-sm text-gray-400 py-3"><Loader2 size={14} className="animate-spin" /> กำลังประมวลผล…</div>}
        </div>
      )}
    </div>
  )
}

// ── Analyze tab ──────────────────────────────────────────────────────────────

function AnalyzeTab({ readyProviders, activeProvider, onSaved }: {
  readyProviders: AiProvider[]; activeProvider: AiProvider | null; onSaved: () => void
}) {
  const [period, setPeriod] = useState<Period>('daily')
  const [fromDate, setFromDate] = useState('')
  const [tillDate, setTillDate] = useState('')
  const [provider, setProvider] = useState<AiProvider | null>(activeProvider)
  const [providerMenu, setProviderMenu] = useState(false)
  useEffect(() => { setProvider(activeProvider) }, [activeProvider])

  const customValid = fromDate !== '' && tillDate !== '' && fromDate <= tillDate
  const canRun = period !== 'custom' || customValid

  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState<string[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [narrative, setNarrative] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [reportId, setReportId] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const run = async () => {
    if (running || !canRun) return
    setRunning(true); setError(null); setProgress([]); setSummary(null); setNarrative(''); setReportId(null)
    const abort = new AbortController()
    abortRef.current = abort
    try {
      const token = localStorage.getItem('token') ?? ''
      const activeCompanyId = localStorage.getItem('activeCompanyId')
      const headers: Record<string, string> = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      if (activeCompanyId) headers['X-Company-Id'] = activeCompanyId
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/servicedesk/analyze`, {
        method: 'POST', headers, signal: abort.signal,
        body: JSON.stringify({
          period, provider: provider ?? undefined,
          ...(period === 'custom' && {
            from: new Date(`${fromDate}T00:00:00`).toISOString(),
            till: new Date(`${tillDate}T23:59:59.999`).toISOString(),
          }),
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }
      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('\n')
        buffer = parts.pop() ?? ''
        for (const line of parts) {
          if (!line.startsWith('data: ')) continue
          let ev: any
          try { ev = JSON.parse(line.slice(6)) } catch { continue }
          if (ev.type === 'progress') setProgress(p => [...p, ev.message])
          else if (ev.type === 'summary') setSummary(ev.summary)
          else if (ev.type === 'text') setNarrative(t => t + ev.text)
          else if (ev.type === 'error') setError(ev.message)
          else if (ev.type === 'done') { setReportId(ev.reportId ?? null); onSaved() }
        }
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') setError(e.message)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">ช่วงเวลา</label>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`px-3.5 py-2 rounded-xl text-sm border transition-all ${
                  period === p.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}>
                <span className="font-medium">{p.label}</span>
                <span className={`block text-[10px] ${period === p.key ? 'text-white/60' : 'text-gray-400'}`}>{p.hint}</span>
              </button>
            ))}
          </div>

          {period === 'custom' && (
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">วันเริ่มต้น</label>
                <input type="date" value={fromDate} max={tillDate || undefined}
                  onChange={e => setFromDate(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">วันสิ้นสุด</label>
                <input type="date" value={tillDate} min={fromDate || undefined}
                  onChange={e => setTillDate(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {fromDate && tillDate && !customValid && (
                <span className="text-xs text-red-500 pb-2">วันเริ่มต้นต้องไม่เกินวันสิ้นสุด</span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button onClick={run} disabled={running || !canRun}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">
            {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
            {running ? 'กำลังวิเคราะห์…' : 'วิเคราะห์ด้วย AI'}
          </button>
          {readyProviders.length > 1 && (
            <div className="relative">
              <button onClick={() => setProviderMenu(v => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full pl-3 pr-2 py-2">
                {provider ? PROVIDER_LABEL[provider] : 'AI'} <ChevronDown size={12} />
              </button>
              {providerMenu && (
                <div className="absolute left-0 top-full mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10">
                  {readyProviders.map(p => (
                    <button key={p} onClick={() => { setProvider(p); setProviderMenu(false) }}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50">
                      {PROVIDER_LABEL[p]} {provider === p && <Check size={13} className="text-blue-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {reportId && !running && <DownloadButtons id={reportId} />}
        </div>

        {running && progress.length > 0 && (
          <div className="text-xs text-gray-500 space-y-1 bg-gray-50 rounded-xl px-3 py-2">
            {progress.map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                {i === progress.length - 1 && running
                  ? <Loader2 size={11} className="animate-spin text-blue-500" />
                  : <Check size={11} className="text-blue-500" />}
                {p}
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-start gap-2">
          <AlertCircle size={15} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {(summary || narrative || running) && (
        <SummaryView summary={summary} narrative={narrative} provider={provider ? PROVIDER_LABEL[provider] : undefined}
          streaming={running && !!summary && !narrative} />
      )}
    </div>
  )
}

// ── History tab ──────────────────────────────────────────────────────────────

function HistoryTab({ isAdmin, refreshKey }: { isAdmin: boolean; refreshKey: number }) {
  const [list, setList] = useState<ReportListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<Period | 'all'>('all')
  const [detail, setDetail] = useState<ReportDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = () => {
    setLoading(true)
    api.get('/servicedesk/reports', { params: filter === 'all' ? {} : { period: filter } })
      .then(r => setList(r.data)).catch(() => setList([])).finally(() => setLoading(false))
  }
  useEffect(load, [filter, refreshKey])

  const open = (id: string) => {
    setDetailLoading(true)
    api.get(`/servicedesk/reports/${id}`).then(r => setDetail(r.data)).catch(() => {}).finally(() => setDetailLoading(false))
  }
  const remove = async (id: string) => {
    if (!confirm('ลบรายงานนี้?')) return
    try { await api.delete(`/servicedesk/reports/${id}`); setList(l => l.filter(r => r.id !== id)); if (detail?.id === id) setDetail(null) }
    catch (e: any) { alert(e.response?.data?.message || 'ลบไม่สำเร็จ') }
  }

  if (detail) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setDetail(null)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
            <ArrowLeft size={15} /> กลับไปรายการ
          </button>
          <span className="text-sm font-semibold text-gray-900">{detail.title}</span>
          <span className="ml-auto"><DownloadButtons id={detail.id} /></span>
        </div>
        <div className="text-xs text-gray-400">
          {fmtDateTime(detail.createdAt)} · {detail.createdByName || 'ผู้ใช้'}
          {detail.provider && <> · AI: {detail.provider}</>}
        </div>
        {detail.status === 'FAILED'
          ? <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">ล้มเหลว: {detail.error}</div>
          : <SummaryView summary={detail.summary} narrative={detail.narrative || ''} provider={detail.provider || undefined} />}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {(['all', 'daily', 'monthly', 'yearly', 'custom'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${filter === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
            {f === 'all' ? 'ทั้งหมด' : PERIOD_LABEL[f]}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-gray-400 hover:text-gray-600"><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
      ) : list.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-10 border border-dashed border-gray-200 rounded-2xl">
          ยังไม่มีประวัติการวิเคราะห์
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-50">
          {detailLoading && <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> กำลังเปิด…</div>}
          {list.map(r => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
              <button onClick={() => open(r.id)} className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800 truncate">{r.title}</span>
                  {r.status === 'FAILED' && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full flex-shrink-0">ล้มเหลว</span>}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {PERIOD_LABEL[r.period]} · {fmtDateTime(r.createdAt)}
                  {r.provider && <> · {r.provider}</>}
                </div>
              </button>
              <button onClick={() => open(r.id)} className="text-xs text-blue-600 hover:underline flex-shrink-0">เปิด</button>
              {r.status !== 'FAILED' && (
                <button onClick={() => downloadWord(r.id).catch(() => alert('ดาวน์โหลดไม่สำเร็จ'))}
                  title="ดาวน์โหลด Word" className="text-gray-400 hover:text-gray-700 flex-shrink-0"><Download size={14} /></button>
              )}
              {r.status !== 'FAILED' && (
                <button onClick={() => openPdf(r.id)} title="เปิด PDF" className="text-gray-400 hover:text-gray-700 flex-shrink-0"><FileText size={14} /></button>
              )}
              {isAdmin && (
                <button onClick={() => remove(r.id)} title="ลบ" className="text-gray-300 hover:text-red-500 flex-shrink-0"><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main panel ───────────────────────────────────────────────────────────────

type TabKey = 'analyze' | 'history'

export default function ServiceDeskAiPanel({ isAdmin = false }: { isAdmin?: boolean }) {
  const [tab, setTab] = useState<TabKey>('analyze')
  const [readyProviders, setReadyProviders] = useState<AiProvider[]>([])
  const [activeProvider, setActiveProvider] = useState<AiProvider | null>(null)
  const [historyKey, setHistoryKey] = useState(0)

  useEffect(() => {
    api.get('/servicedesk/providers')
      .then(r => { setReadyProviders(r.data.ready ?? []); setActiveProvider(r.data.active ?? null) })
      .catch(() => {})
  }, [])

  const TABS: { key: TabKey; label: string; icon: any }[] = [
    { key: 'analyze', label: 'วิเคราะห์ใหม่', icon: Sparkles },
    { key: 'history', label: 'ประวัติ', icon: History },
  ]

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-800 flex gap-2">
        <Info size={15} className="flex-shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p><strong>วิเคราะห์ ServiceDesk ด้วย AI</strong> — สรุปปริมาณงาน, SLA compliance, เวลาแก้ไข และแนวโน้มหมวดหมู่ปัญหา แล้วให้ AI เขียนรายงานภาษาไทย</p>
          <p>• เก็บประวัติอัตโนมัติ · ดาวน์โหลดเป็น Word / PDF ได้ · เลือกช่วงรายวัน / รายเดือน / รายปี</p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 border-b border-gray-200">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'analyze' && (
        <AnalyzeTab readyProviders={readyProviders} activeProvider={activeProvider}
          onSaved={() => setHistoryKey(k => k + 1)} />
      )}
      {tab === 'history' && <HistoryTab isAdmin={isAdmin} refreshKey={historyKey} />}
    </div>
  )
}
